
export type FeedBackData = { waitMessages?: string[], estimatedTime?: number, element?: HTMLElement }

export interface IModuleLoaderItem {
    instance: () => Promise<any>
    css: () => Promise<any>
    html: () => Promise<any>
}

export interface INotificationItem {
    key: any
    description: string
}

export interface INotificationSource {
    name: string
    subscribe(callback: (notifier: INotificationSource, result: INotificationItem[]) => void): void
    action(): void
}

export interface IModules {
    [name: string]: IModuleLoaderItem
}

export type INotifiers = { Notifier: INotificationSource }[]

export interface IEventEmitter {

}

export class LazyLoadFacet {
    public timer?: any
    public delay: number
    constructor(ADelay: number) {
        this.delay = ADelay
    }
}

export enum CommandFlags {
    objectAction = 1 << 0,
    defaultEdit = 2 << 0
}

export interface ICommand {
    tag: number,
    id: string,
    data: string,
    vars?: string[],
    varValues?: { [index: string]: any },
    caption: string,
    iconClass?: string,
    flags?: CommandFlags
    isDropDown?: boolean
};

export interface IMillenniumFrame {
    readonly element: HTMLElement
    readonly browser: IMillenniumBrowser
    readonly caption: string
    setCaption(value: string): void
    setUrl(value: string): void
    moduleName: string
    navigate(data: string, vars: Object): void
    navigateSc(key: string, data: string, vars: Object, caption?: any): void
    close(): Promise<void>
    activate(): void | Promise<void>
    deactivate(): void | Promise<void>
    activity<T extends PromiseLike<unknown>>(waiter: T, feedBackData?: FeedBackData): T
    lazyLoad(lazyFacet: LazyLoadFacet, callback: () => void): void
    notify(message: string, type?: 'warning' | 'danger' | 'success' | 'warning-auto-hide'): void
    requestFullScreen(): void
    accessPrompt(message: string, key: string, userInfo?: any): Promise<boolean>
    /**
    * Verifica se o usuário tem acesso com base na chave fornecida.
    * 
    * @function
    * @param {string} key - A chave de acesso a ser verificada.
    * @returns {Promise<boolean>} Uma Promise que resolve para `true` se o acesso for permitido, caso contrário, `false`.
    */
    hasAccess(key: string): Promise<boolean>
    loadExtension(name: string): Promise<any>
    key: string
    route: IRoute | undefined
}

export interface IRoute {
    text: string
    target: string
    /** Target secundário/experimental (rota alternativa). Presente quando o link declara `target2`
     *  e tanto o primário quanto o secundário são compatíveis com a plataforma atual (HTML).
     *  Quando presente, o cliente abre o primário e oferece testar este; a escolha é lembrada. */
    target2?: string
    urlKey: string
    icon?: string
    caption?: string
    params?: any[]
    key?: string
    nodes?: IRoute[]
}

export enum MsgBoxFlags {
    MB_OK = 0x00000000,
    MB_OKCANCEL = 0x00000001,
    MB_ABORTRETRYIGNORE = 0x00000002,
    MB_YESNOCANCEL = 0x00000003,
    MB_YESNO = 0x00000004,
    MB_RETRYCANCEL = 0x00000005,

    MB_ICONERROR = 0x00000010,
    MB_ICONQUESTION = 0x00000020,
    MB_ICONWARNING = 0x00000030,
    MB_ICONINFORMATION = 0x00000040,

    MB_DEFBUTTON1 = 0x00000000,
    MB_DEFBUTTON2 = 0x00000100,
    MB_DEFBUTTON3 = 0x00000200,
    MB_DEFBUTTON4 = 0x00000300
}

export enum MsgBoxResult {
    IDOK = 1,
    IDCANCEL = 2,
    IDABORT = 3,
    IDRETRY = 4,
    IDIGNORE = 5,
    IDYES = 6,
    IDNO = 7,
    IDCLOSE = 8
}

export interface IMillenniumNativeFrame {
    resize(left: number, top: number, width: number, height: number, visible: number): void
    navigate(place: string): void
    destroy(): void
}

export interface IModalWindow {
    show(): Promise<any>
    close(result: any): void
}

export interface IModalWindowCommand {
    caption: string
    id?: any
    callback?: () => void
}

export interface IModalWindowOptions {
    title: string
    width?: string
    height?: string
    minWidth?: string
    minHeight?: string
    maxWidth?: string
    maxHeight?: string
    commands: IModalWindowCommand[]
}

export interface IMillenniumBrowser {
    readonly isEmbedded: boolean
    /** Preferência de onboarding do cliente nativo (binbrowser): `false` = desabilitado pelo menu. Ausente no web. */
    readonly onboardingEnabled?: boolean
    /**
     * Disponibilidade do broker (servidor central), detectada pelo heartbeat do browser.
     * `true` = online; `false` = em contingência. Materializa também `Client.onLine`.
     */
    readonly online: boolean
    /** Subscreve mudanças de disponibilidade do broker; retorna função para cancelar a inscrição. */
    onlineChange(callback: (online: boolean) => void): () => void
    /**
     * Rota inicial opcional, injetada por um shell dedicado (ex.: o bundle de contingência do Omni)
     * ANTES do start(). Ausente → a tela inicial é a "home" do core. Mantém o core agnóstico de
     * páginas de outras libs — quem decide a página de entrada é o shell que monta o browser.
     */
    startupRoute?: IRoute
    readonly userName: string
    readonly userGroup: string
    readonly wsid: string
    readonly inLogin: boolean
    joinSession(host: string, sessionId: string, sessionData: any, joinOnServer: boolean, appName?: string, licenceName?: string): PromiseLike<void>
    load(frame: IMillenniumFrame, routeKey: string | undefined, routeObj: any, vars?: any): PromiseLike<void>
    getRoutes(): PromiseLike<IRoute[]>
    getRoute(key: string): PromiseLike<IRoute>
    notify(message: string | Error, type?: 'warning' | 'danger' | 'success' | 'warning-auto-hide'): void
    activity<T extends PromiseLike<any>>(waiter: T, element: HTMLElement, feedBackData?: FeedBackData, silent?: boolean): T
    login(appname: string, licenceName: string, host: string, userName: string, passWord: string): PromiseLike<any>
    switchUser(): Promise<void>
    accessPrompt(message: string, key: string, userInfo?: any): PromiseLike<boolean>
    hasAccess(key: string): Promise<boolean>
    logout(): PromiseLike<void>
    getSessionId(): Promise<string>
    navigate(place: string, vars: Object): Promise<void>
    navigateSc(key: string, data: string, vars: Object, caption?: any): Promise<void>
    getData(dataType: string, callback: (data: string) => void): void
    getVersion(): PromiseLike<{ [moduleName: string]: string | undefined }>
    getObjectActions(objectName: string): PromiseLike<ICommand[]>
    loadExtension(module: string): Promise<any>
    broadcast(subject: string, data: any): void
    subscribeAdd(subject: string, callback: (data: any) => void): void
    subscribeRemove(subject: string, callback: (data: any) => void): void
    printPDF(pdfAsBlob: Blob): void
    savePDF(pdfAsBlob: Blob): void
    saveFile(fileAsBlob: Blob, aType: string, fileName: string): void
    msgBox(message: string, title: string, flags: MsgBoxFlags): Promise<MsgBoxResult>
    createFrame(): IMillenniumFrame
    createFrameAsNative(): Promise<IMillenniumNativeFrame>
    createModal(content: HTMLElement, options: IModalWindowOptions): IModalWindow
    hasFrame(key: string): boolean
    hideModuleHeader(): void
    showModuleHeader(): void
    currentModuleName: string
    openFavoritesManageDialog(): Promise<void>
}

export interface IMillennium {
    browser: IMillenniumBrowser
    /** Modo de contingência: marcado por `millennium.offline` (importado pelo boot do shell dedicado)
     *  antes do boot do browser. Faz o boot pular login/ACL/notificações e usar recursos locais. */
    offline?: boolean
    lib: {
        "wts.ui.controls": Promise<typeof import("lib/wts.ui.controls")>
        "wts.ui.templating": Promise<typeof import("lib/wts.ui.templating")>
        "wts.ui.templating.lit": Promise<typeof import("lib/wts.ui.templating.lit")>
        "wts.ui.overlays": Promise<typeof import("lib/wts.ui.overlays")>
        "wts.ui.onboarding": Promise<typeof import("lib/wts.ui.onboarding")>
        "wts.ui.feedback": Promise<typeof import("lib/wts.ui.feedback")>
        "wts.client": Promise<typeof import("lib/wts.client")>
        "wts.ui.data": Promise<typeof import("lib/wts.ui.data")>
        "wts.ui.frame": Promise<typeof import("lib/wts.ui.frame")>
        "wts.ui.dataform": Promise<typeof import("lib/wts.ui.dataform")>
        "wts.ui.editors.slick": Promise<typeof import("lib/wts.ui.editors.slick")>
        "wts.ui.editors": Promise<typeof import("lib/wts.ui.editors")>
        // Vendor libs síncronas (não-Promise): consumidas por scripts CJS/AMD legacy que
        // requerem KO/jQuery síncronos. Extensões importam via externals em modo `var`.
        // Opcionais no tipo porque são atribuídas via Object.assign após o objeto literal.
        jQuery?: any
        $?: any
        ko?: any
        moment?: any
        "lit-html": Promise<typeof import("lit-html")>
        "lit-html/directives/repeat.js": Promise<typeof import("lit-html/directives/repeat.js")>
        "lit-html/directives/unsafe-html.js": Promise<typeof import("lit-html/directives/unsafe-html.js")>
        "lit-html/directives/class-map.js": Promise<typeof import("lit-html/directives/class-map.js")>
        "lit-html/private-ssr-support.js": Promise<typeof import("lit-html/private-ssr-support.js")>
    }
    params: Map<string, string>
}

declare global {
    const millennium: IMillennium
}
